import { useHead } from '@unhead/vue'
import {
    computed,
    watch,
    ref,
    provide,
    inject,
    type Ref,
    readonly,
    onMounted,
    onUnmounted
} from 'vue'
import { useI18n } from 'vue-i18n'

const RECAPTCHA_INJECTION_KEY = 'RECAPTCHA_INJECTION_KEY'

declare global {
    interface Window {
        RedpointRecaptchaLoad?: () => void
        RedpointRecaptchaReady?: boolean
        RedpointRecaptchaReadyFunction?: () => void
    }
}

export function useRecaptchaReady(): Readonly<Ref<boolean>> {
    return inject(RECAPTCHA_INJECTION_KEY, ref(false))
}

export function createRecaptcha() {
    const { locale } = useI18n({ useScope: 'global' })
    // Keep the reCAPTCHA locale mapping explicit so supported language keys
    // are easy to audit as storefront localization options evolve.
    const lang = computed(() => {
        if (locale.value.startsWith('es-')) {
            return 'es'
        }

        if (locale.value.startsWith('fr-')) {
            return 'fr'
        }

        if (locale.value.startsWith('de-')) {
            return 'de'
        }

        return undefined
    })

    const url = computed(() => {
        return (
            'https://www.google.com/recaptcha/api.js?onload=RedpointRecaptchaLoad&render=explicit' +
            (lang.value !== undefined ? '&hl=' + lang.value : '')
        )
    })

    const ready = ref(false)

    function handleRedpointRecaptchaLoad() {
        // Google calls this named global from api.js; define it in bundled
        // JavaScript so the CSP does not need to allow inline scripts.
        window.RedpointRecaptchaReady = true
        ready.value = true

        if (typeof window.RedpointRecaptchaReadyFunction === 'function') {
            window.RedpointRecaptchaReadyFunction()
        }
    }

    if (process.client) {
        // Register the callback before adding the Google script tag on the client.
        window.RedpointRecaptchaLoad = handleRedpointRecaptchaLoad
    }

    watch(
        url,
        () => {
            ready.value = false
            // Locale can be corrected during SSR before browser globals exist.
            // Only reset the browser reCAPTCHA flag once we are on the client.
            if (process.client) {
                window.RedpointRecaptchaReady = false
            }
        },
        { flush: 'sync' }
    )

    provide(RECAPTCHA_INJECTION_KEY, readonly(ready))

    onMounted(() => {
        if (window.RedpointRecaptchaReady) {
            ready.value = true
        }
        window.RedpointRecaptchaReadyFunction = () => {
            ready.value = true
        }
    })

    onUnmounted(() => {
        window.RedpointRecaptchaReadyFunction = undefined
    })

    if (process.client) {
        useHead({
            script: () => [
                {
                    src: url.value,
                    async: true,
                    defer: true
                }
            ]
        })
    }
}
